import { useState, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import SearchFilterBar from './SearchFilterBar';
import AddFilterMenu, { FilterMenuOption } from './AddFilterMenu';
import TitleModal from './modals/TitleModal';
import CompetenceModal from './modals/CompetenceModal';
import CountryFilterModal from './modals/CountryFilterModal';
import MatterLeadFilterModal from './modals/MatterLeadFilterModal';
import ClientSearchModal from './modals/ClientSearchModal';
import ExperienceFacetModal from './modals/ExperienceFacetModal';
import { SearchFilterProps, FilterState, ActiveFilter } from './types';
import { UserSearchParams } from 'utils/wemble-api.generated';
import { useGetCurrentUserQuery, useGetOfficesQuery, useGetExperienceFacetsQuery } from 'utils/wemble-api';
import usePermissions from 'hooks/usePermissions';
import { trackAction } from 'utils/trackAction';
import { getUserOfficeIds, loadSavedFilter, saveFilter, arraysEqual } from './filterPersistence';
import { DIMENSION_CONFIG } from 'components/shared/experienceDimensions';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import PersonIcon from '@material-ui/icons/Person';
import PublicIcon from '@material-ui/icons/Public';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import WorkIcon from '@material-ui/icons/Work';
import DescriptionIcon from '@material-ui/icons/Description';
import CategoryIcon from '@material-ui/icons/Category';
import competencesIcon from 'assets/icons/competences.svg';
import React from 'react';

type ModalType = 'team' | 'title' | 'competence' | 'country' | 'matterLead' | 'client' | 'workType' | 'industry' | 'practiceArea' | null;

const CompetenceIcon = () => React.createElement('img', { src: competencesIcon, alt: '', style: { width: 20, height: 20, opacity: 0.8 } });

const BASE_MENU_OPTIONS: FilterMenuOption[] = [
  { type: 'team', label: 'Group', description: 'Filter by group and office', icon: React.createElement(AccountTreeIcon) },
  { type: 'title', label: 'Title', description: 'Filter by role or title', icon: React.createElement(PersonIcon) },
  { type: 'competence', label: 'Competency', description: 'Filter by competency and skill level', icon: React.createElement(CompetenceIcon) },
];

const EXPERIENCE_MENU_OPTIONS: FilterMenuOption[] = [
  { type: 'country', label: 'Client Countries', description: 'Find people with client country experience', icon: React.createElement(PublicIcon), dimension: 'country' },
  { type: 'matterLead', label: 'Responsible Partner', description: 'Find people who worked under a specific partner', icon: React.createElement(SupervisorAccountIcon), dimension: 'partner' },
  { type: 'client', label: 'Client', description: 'Find people who worked for a specific client', icon: React.createElement(WorkIcon), dimension: 'client' },
  { type: 'workType', label: 'Work Type', description: 'Find people by type of legal work', icon: React.createElement(DescriptionIcon), dimension: 'workType' },
  { type: 'industry', label: 'Industry', description: 'Find people by client industry', icon: React.createElement(CategoryIcon), dimension: 'industry' },
  { type: 'practiceArea', label: 'Practice Area', description: 'Find people by practice area (experience data from matters)', icon: React.createElement(AccountTreeIcon), dimension: 'practiceArea' },
];

const SearchFilter = ({ facets, onChange, onSearchTermChange }: SearchFilterProps) => {
  const { data: currentUser } = useGetCurrentUserQuery();
  const [experienceScope, setExperienceScope] = useState<'allTime' | 'pastYear' | 'pastMonth'>('pastYear');
  const [experienceOperator, setExperienceOperator] = useState<'and' | 'or'>('and');

  const [initialized, setInitialized] = useState(false);
  const [filterState, setFilterState] = useState<FilterState>({
    teams: ['__all__'],
    advancedFilters: [],
    competenceOperator: 'and',
    searchTerm: '',
  });

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const defaultTeamsRef = useRef<string[]>(['__all__']);

  const { data: offices } = useGetOfficesQuery();
  const { org, canViewExperience: showExperience } = usePermissions();

  // Pass current team/title filters so facets narrow to the visible population
  const facetTeams = filterState.teams[0] !== '__all__' ? filterState.teams.join(',') : undefined;
  const facetTitles = (() => {
    const titleFilter = filterState.advancedFilters.find(f => f.type === 'title');
    if (titleFilter?.type === 'title') return titleFilter.titles.join(',');
    return undefined;
  })();
  const { data: experienceFacets } = useGetExperienceFacetsQuery({ scope: experienceScope, teams: facetTeams, titles: facetTitles }, { skip: !showExperience });

  // Initialize: restore from localStorage or compute defaults
  useEffect(() => {
    if (currentUser && offices && !initialized) {
      const userOfficeIds = getUserOfficeIds(currentUser);

      // Compute defaults first (needed for both restore and fresh start)
      const groupId = typeof currentUser.group === 'string' ? currentUser.group : currentUser.group?._id;
      const secondaryGroupId = typeof currentUser.secondaryGroup === 'string' ? currentUser.secondaryGroup : (currentUser.secondaryGroup as any)?._id;

      let defaultTeams: string[];
      if (org.allGroupsDefault) {
        defaultTeams = ['__all__'];
      } else if (org.allOfficesDefault && groupId) {
        const groupIds = [groupId, secondaryGroupId].filter(Boolean);
        const groupOfficeIds = offices?.filter(o => {
          const oGroupId = typeof o.group === 'string' ? o.group : o.group?._id;
          return groupIds.includes(oGroupId);
        }).map(o => o._id!).filter(Boolean) || [];
        defaultTeams = groupOfficeIds.length > 0 ? groupOfficeIds : ['__all__'];
      } else if (userOfficeIds.length > 0) {
        defaultTeams = userOfficeIds;
      } else {
        defaultTeams = ['__all__'];
      }
      defaultTeamsRef.current = defaultTeams;

      // Try restoring saved filter
      const allOfficeIds = offices.map(o => o._id!).filter(Boolean);
      const saved = loadSavedFilter(userOfficeIds, allOfficeIds);
      if (saved) {
        setFilterState(prev => ({ ...prev, ...saved }));
        setInitialized(true);
        return;
      }

      setFilterState(prev => ({ ...prev, teams: defaultTeams }));
      setInitialized(true);
    }
  }, [currentUser, offices, initialized]);

  // ?group=<id> (a group name clicked on a person card): show that group's teams, no other filters.
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const groupId = new URLSearchParams(location.search).get('group');
    if (!groupId || !offices || !initialized) return;
    const teams = offices
      .filter(o => (typeof o.group === 'string' ? o.group : o.group?._id) === groupId)
      .map(o => o._id!)
      .filter(Boolean);
    if (teams.length) setFilterState(prev => ({ ...prev, teams, advancedFilters: [], searchTerm: '' }));
    history.replace({ pathname: location.pathname, search: '' });
  }, [location.search, offices, initialized]);

  // Persist filter state to localStorage on change
  useEffect(() => {
    if (initialized && currentUser) {
      saveFilter(filterState, getUserOfficeIds(currentUser), defaultTeamsRef.current);
    }
  }, [filterState, initialized, currentUser]);

  // Serialize to UserSearchParams
  const searchParams: UserSearchParams = useMemo(() => {
    const params: UserSearchParams = {};

    if (filterState.teams.length && filterState.teams[0] !== '__all__') {
      params.teams = filterState.teams;
    }

    const titleFilters = filterState.advancedFilters.filter(f => f.type === 'title');
    if (titleFilters.length) {
      params.titles = titleFilters.flatMap(f => f.type === 'title' ? f.titles : []);
    }

    const competenceFilters = filterState.advancedFilters.filter(f => f.type === 'competence');
    if (competenceFilters.length) {
      params.competences = competenceFilters
        .filter((f): f is ActiveFilter & { type: 'competence' } => f.type === 'competence')
        .map(f => ({ competenceId: f.competenceId, minLevel: f.minLevel }));
      params.competenceOperator = filterState.competenceOperator;
    }

    const experienceFilters = filterState.advancedFilters.filter(f => f.type === 'experience');
    if (experienceFilters.length) {
      params.experience = experienceFilters
        .filter((f): f is ActiveFilter & { type: 'experience' } => f.type === 'experience')
        .flatMap(f => f.keys.map((key, i) => ({ dimension: f.dimension, key, label: f.labels[i], minHours: f.minHours || undefined })));
      params.experienceScope = experienceScope;
      params.experienceOperator = experienceOperator;
    }

    return params;
  }, [filterState.teams, filterState.advancedFilters, filterState.competenceOperator, experienceScope, experienceOperator]);

  useEffect(() => {
    if (initialized) {
      onChange(searchParams);
    }
  }, [searchParams, initialized]);

  useEffect(() => {
    if (onSearchTermChange) {
      onSearchTermChange(filterState.searchTerm);
    }
  }, [filterState.searchTerm]);

  const menuCloseTimer = useRef<number>(0);

  // --- Handlers ---

  const handleAdvancedFilterClick = (index: number) => {
    const filter = filterState.advancedFilters[index];
    if (filter.type === 'title') setActiveModal('title');
    if (filter.type === 'competence') setActiveModal('competence');
    if (filter.type === 'experience' && filter.dimension === 'country') setActiveModal('country');
    if (filter.type === 'experience' && filter.dimension === 'partner') setActiveModal('matterLead');
    if (filter.type === 'experience' && filter.dimension === 'client') setActiveModal('client');
    if (filter.type === 'experience' && filter.dimension === 'workType') setActiveModal('workType');
    if (filter.type === 'experience' && filter.dimension === 'industry') setActiveModal('industry');
    if (filter.type === 'experience' && filter.dimension === 'practiceArea') setActiveModal('practiceArea');
  };

  const handleAdvancedFilterRemove = (index: number) => {
    setFilterState(prev => ({
      ...prev,
      advancedFilters: prev.advancedFilters.filter((_, i) => i !== index),
    }));
  };

  const handleMenuSelect = (type: string) => {
    setActiveModal(type as ModalType);
  };

  const isNonDefault = filterState.advancedFilters.length > 0 ||
    !arraysEqual(filterState.teams, defaultTeamsRef.current);

  // Track only active searches — user applied a non-default filter.
  // The initial default search on app open isn't user intent, skip it.
  useEffect(() => {
    if (initialized && isNonDefault) {
      trackAction('search', searchParams);
    }
  }, [searchParams, initialized, isNonDefault]);

  const handleReset = () => {
    setFilterState({
      teams: defaultTeamsRef.current,
      advancedFilters: [],
      competenceOperator: 'and',
        searchTerm: '',
    });
  };

  return (
    <>
      <SearchFilterBar
        teams={filterState.teams}
        teamFacets={facets?.teams}
        onTeamsChange={(teams) => setFilterState(prev => ({ ...prev, teams }))}
        advancedFilters={filterState.advancedFilters}
        competenceFacets={facets?.competences}
        onAdvancedFilterClick={handleAdvancedFilterClick}
        onAdvancedFilterRemove={handleAdvancedFilterRemove}
        onAddFilter={(el) => setMenuAnchor(el)}
        onAddFilterHover={(el) => { clearTimeout(menuCloseTimer.current); setMenuAnchor(el); }}
        onAddFilterLeave={() => { menuCloseTimer.current = window.setTimeout(() => setMenuAnchor(null), 150); }}
        teamModalOpen={activeModal === 'team'}
        onTeamModalClose={() => setActiveModal(null)}
        isNonDefault={isNonDefault}
        onReset={handleReset}
      />

      <AddFilterMenu
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
        onCancelClose={() => clearTimeout(menuCloseTimer.current)}
        onSelect={handleMenuSelect}
        options={[
          ...BASE_MENU_OPTIONS,
          ...(showExperience ? EXPERIENCE_MENU_OPTIONS.filter(o => {
            if (!experienceFacets?.length) return false;
            return experienceFacets.some(f => f.dimension === (o as any).dimension);
          }) : []),
        ]}
      />

      <TitleModal
        visible={activeModal === 'title'}
        onClose={() => setActiveModal(null)}
        onSave={(titles) => {
          setFilterState(prev => {
            const withoutTitle = prev.advancedFilters.filter(f => f.type !== 'title');
            return {
              ...prev,
              advancedFilters: titles.length > 0
                ? [...withoutTitle, { type: 'title' as const, titles }]
                : withoutTitle,
            };
          });
        }}
        options={facets?.titles || []}
        initialSelected={
          filterState.advancedFilters.find(f => f.type === 'title')?.type === 'title'
            ? (filterState.advancedFilters.find(f => f.type === 'title') as any).titles
            : []
        }
      />

      <CompetenceModal
        visible={activeModal === 'competence'}
        onClose={() => setActiveModal(null)}
        onSave={(selections, op) => {
          setFilterState(prev => {
            const withoutCompetence = prev.advancedFilters.filter(f => f.type !== 'competence');
            const newFilters: ActiveFilter[] = selections.map(s => ({
              type: 'competence' as const,
              competenceId: s.competenceId,
              minLevel: s.minLevel,
            }));
            return {
              ...prev,
              advancedFilters: [...withoutCompetence, ...newFilters],
              competenceOperator: op,
            };
          });
        }}
        competences={facets?.competences || []}
        initialOperator={filterState.competenceOperator}
        initialSelected={
          filterState.advancedFilters
            .filter((f): f is ActiveFilter & { type: 'competence' } => f.type === 'competence')
            .map(f => ({ competenceId: f.competenceId, minLevel: f.minLevel }))
        }
      />

      <CountryFilterModal
        visible={activeModal === 'country'}
        onClose={() => setActiveModal(null)}
        initialScope={experienceScope}
        initialOperator={experienceOperator}
        onScopeChange={setExperienceScope}
        onSave={(keys, labels, scope, op) => {
          setExperienceScope(scope);
          setExperienceOperator(op);
          setFilterState(prev => {
            const without = prev.advancedFilters.filter(f => !(f.type === 'experience' && f.dimension === 'country'));
            return {
              ...prev,
              advancedFilters: keys.length > 0
                ? [...without, { type: 'experience' as const, dimension: 'country', keys, labels }]
                : without,
            };
          });
        }}
        options={(experienceFacets || []).filter(f => f.dimension === 'country')}
        initialSelected={
          (filterState.advancedFilters.find(f => f.type === 'experience' && f.dimension === 'country') as any)?.keys || []
        }
      />

      <MatterLeadFilterModal
        visible={activeModal === 'matterLead'}
        onClose={() => setActiveModal(null)}
        initialScope={experienceScope}
        initialOperator={experienceOperator}
        onScopeChange={setExperienceScope}
        onSave={(keys, labels, scope, op) => {
          setExperienceScope(scope);
          setExperienceOperator(op);
          setFilterState(prev => {
            const without = prev.advancedFilters.filter(f => !(f.type === 'experience' && f.dimension === 'partner'));
            return {
              ...prev,
              advancedFilters: keys.length > 0
                ? [...without, { type: 'experience' as const, dimension: 'partner', keys, labels }]
                : without,
            };
          });
        }}
        options={(experienceFacets || []).filter(f => f.dimension === 'partner')}
        initialSelected={
          (filterState.advancedFilters.find(f => f.type === 'experience' && f.dimension === 'partner') as any)?.keys || []
        }
      />

      <ClientSearchModal
        visible={activeModal === 'client'}
        onClose={() => setActiveModal(null)}
        initialScope={experienceScope}
        initialOperator={experienceOperator}
        onScopeChange={setExperienceScope}
        initialMinHours={(filterState.advancedFilters.find(f => f.type === 'experience' && f.dimension === 'client') as any)?.minHours || 0}
        countsFor={key => (experienceFacets || []).find(f => f.dimension === 'client' && f.key === key)}
        teams={facetTeams}
        titles={facetTitles}
        onSave={(keys, labels, scope, op, minHours) => {
          setExperienceScope(scope);
          setExperienceOperator(op);
          setFilterState(prev => {
            const without = prev.advancedFilters.filter(f => !(f.type === 'experience' && f.dimension === 'client'));
            return {
              ...prev,
              advancedFilters: keys.length > 0
                ? [...without, { type: 'experience' as const, dimension: 'client', keys, labels, minHours }]
                : without,
            };
          });
        }}
        initialSelected={
          (() => {
            const filter = filterState.advancedFilters.find(f => f.type === 'experience' && f.dimension === 'client') as any;
            if (!filter) return [];
            return filter.keys.map((k: string, i: number) => ({ key: k, label: filter.labels[i] }));
          })()
        }
      />

      {/* Work Type / Industry / Practice Area — generic facet modals */}
      {(['workType', 'industry', 'practiceArea'] as const).map(dim => {
        const config = DIMENSION_CONFIG[dim];
        if (!config) return null;
        return (
          <ExperienceFacetModal
            key={dim}
            visible={activeModal === dim}
            onClose={() => setActiveModal(null)}
            initialScope={experienceScope}
            initialOperator={experienceOperator}
            onScopeChange={setExperienceScope}
            initialMinHours={(filterState.advancedFilters.find(f => f.type === 'experience' && f.dimension === dim) as any)?.minHours || 0}
            title={config.title}
            description={config.description('each person')}
            color={config.color}
            searchPlaceholder={`Search ${config.title.toLowerCase()}...`}
            emptyLabel={`No ${config.title.toLowerCase()} selected`}
            onSave={(keys, labels, scope, op, minHours) => {
              setExperienceScope(scope);
              setExperienceOperator(op);
              setFilterState(prev => {
                const without = prev.advancedFilters.filter(f => !(f.type === 'experience' && f.dimension === dim));
                return {
                  ...prev,
                  advancedFilters: keys.length > 0
                    ? [...without, { type: 'experience' as const, dimension: dim, keys, labels, minHours }]
                    : without,
                };
              });
            }}
            options={(experienceFacets || []).filter(f => f.dimension === dim)}
            initialSelected={
              (filterState.advancedFilters.find(f => f.type === 'experience' && f.dimension === dim) as any)?.keys || []
            }
          />
        );
      })}
    </>
  );
};

export default SearchFilter;
